/** @jsx jsx */
import React, { useMemo, useState } from 'react';
import { jsx } from '@emotion/core';
import useTranslate, {
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';
import styles from '../styles';
import ReactDOMServer from 'react-dom/server';
import JsBarcode from 'jsbarcode';
import canUseDOM from 'utils/canUseDOM';

interface ContentPlacementPropTypes {
  i18nKey: string;
  i18nContext?: string;
  tenantCode?: string;
}

/**
 * Placement for translated copy. The translation can have
 * - one level of heading (wrapped with "<0></0>") (block element)
 * - basic HTML tags without attributes and at most one text child.
 * See documentation for react-i18next's Trans component for more details.
 *
 * For example:
 * "<0>My Heading</0><p><strong>Strong text</strong></p><p>Normal text</p>".
 */
const ContentPlacement: React.FC<ContentPlacementPropTypes> = ({
  i18nKey,
  i18nContext,
  tenantCode,
}) => {
  const tMdMultiline = useMarkdownMultilineTranslate();
  const t = useTranslate();
  const [barcodeWidth, setBarcodeWidth] = useState<number>(300);
  const [barcodeHeight, setBarcodeHeight] = useState<number>(150);

  const barcodeSvgString = (value: string) => {
    const svg = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg',
    ) as SVGSVGElement;
    JsBarcode(svg, value, { format: 'CODE128', displayValue: false });
    if (svg.width) setBarcodeWidth(svg.width.baseVal.value);
    if (svg.height) setBarcodeHeight(svg.height.baseVal.value);
    const html = svg.innerHTML;
    svg.remove();
    return html;
  };
  const barcodeComponent = useMemo(() => {
    if (tenantCode === undefined) return null; //TenantCode should be defined at this stage, but barcodeSvgString complains without the check
    if (!canUseDOM) return null;
    return ReactDOMServer.renderToString(
      <div>
        <svg
          data-testid="tenantCodeBarcode"
          width={barcodeWidth}
          height={barcodeHeight}
          dangerouslySetInnerHTML={{ __html: barcodeSvgString(tenantCode) }}
        />
      </div>,
    );
  }, [tenantCode, barcodeWidth, barcodeHeight]);

  if (!t(i18nKey)) {
    return null;
  }

  const placementContent = tMdMultiline(i18nKey, { context: i18nContext });
  const placementTenantCodeContent = tenantCode
    ? tMdMultiline(`${i18nKey}TenantCode`, {
        context: i18nContext,
        barCode: barcodeComponent,
        tenantCode: tenantCode,
      })
    : null;

  return (
    <div css={styles.dspContentPlacement}>
      {placementContent && (
        <div data-testid="placementContent">{placementContent}</div>
      )}
      {placementTenantCodeContent && (
        <div data-testid="placementTenantCodeContent">
          {placementTenantCodeContent}
        </div>
      )}
    </div>
  );
};

export default ContentPlacement;
